import type * as FirebaseClientModule from '@akpl/firebase/client';
import type { FirebaseClient, FirebaseClientConfig } from '@akpl/firebase/client';

let client: FirebaseClient | null = null;
let sdk: Promise<typeof FirebaseClientModule> | null = null;

/**
 * Starts downloading the Firebase chunk without waiting for it (QA 2026-09-14, MOB-56).
 *
 * `loadFirebase` needs `/config` before it can initialize anything, but the 354 KB chunk does
 * not: kicking the import off next to the `/config` request instead of after it takes a whole
 * network round-trip off the boot path. The module registry de-duplicates, so `loadFirebase`
 * reuses this exact promise.
 */
export function prefetchFirebase(): void {
  if (sdk) return;
  sdk = import('@akpl/firebase/client');
  // The real failure is reported by `loadFirebase`; this only keeps a prefetch that nobody is
  // awaiting yet from surfacing as an unhandled rejection.
  void sdk.catch(() => undefined);
}

/**
 * Lazily loads the Firebase SDK (its own chunk) and initializes the client with
 * **`browserLocalPersistence` first** (docs/05 §1): iOS Safari evicts IndexedDB in private
 * browsing and after long inactivity, which would sign a singer out mid-session.
 *
 * Returns null when `/config` carries no Firebase project, which puts the shell in the same
 * "offline auth" mode the TV uses.
 */
export async function loadFirebase(config: FirebaseClientConfig): Promise<FirebaseClient | null> {
  if (client) return client;
  sdk ??= import('@akpl/firebase/client');
  const mod = await sdk;
  if (!mod.hasFirebaseConfig(config)) return null;
  client = mod.initFirebase(config, { persistence: 'local-first' });
  return client;
}

/** The client once loaded (auth provider, `useSession` listeners). */
export function getFirebase(): FirebaseClient | null {
  return client;
}

/** Test helper. */
export function setFirebaseForTests(next: FirebaseClient | null): void {
  client = next;
  sdk = null;
}
